// SkyTracker @skytracker/web — Zustand store for fleet + selection + filters.
import type { Bbox, ClientFilters, FeedStatus, Position } from '@skytracker/shared';
import { create } from 'zustand';

interface FleetState {
  fleet: Map<string, Position>;
  selectedId: string | null;
  bbox: Bbox;
  filters: ClientFilters;
  feedStatus: FeedStatus[];
  truncated: { count: number; sampled_by: 'altitude' } | null;
  panelLeftOpen: boolean;
  fallbackActive: boolean;
  applySnapshot: (positions: Position[], feed: FeedStatus[], truncated: FleetState['truncated']) => void;
  applyUpdate: (positions: Position[]) => void;
  applyRemove: (ids: string[]) => void;
  setBbox: (bbox: Bbox) => void;
  setFilters: (filters: ClientFilters) => void;
  setSelected: (id: string | null) => void;
  setFeedStatus: (s: FeedStatus[]) => void;
  setPanelLeftOpen: (open: boolean) => void;
  markFallbackActive: () => void;
}

const NA_BBOX: Bbox = [-130, 24, -65, 60];

export const useFleet = create<FleetState>((set) => ({
  fleet: new Map(),
  selectedId: null,
  bbox: NA_BBOX,
  filters: {},
  feedStatus: [],
  truncated: null,
  panelLeftOpen: false,
  fallbackActive: false,
  applySnapshot: (positions, feedStatus, truncated) =>
    set(() => {
      const m = new Map<string, Position>();
      for (const p of positions) m.set(p.id, p);
      return { fleet: m, feedStatus, truncated };
    }),
  applyUpdate: (positions) =>
    set((state) => {
      const m = new Map(state.fleet);
      for (const p of positions) m.set(p.id, p);
      return { fleet: m };
    }),
  applyRemove: (ids) =>
    set((state) => {
      const m = new Map(state.fleet);
      for (const id of ids) m.delete(id);
      return { fleet: m };
    }),
  setBbox: (bbox) => set({ bbox }),
  setFilters: (filters) => set({ filters }),
  setSelected: (id) => set({ selectedId: id }),
  setFeedStatus: (feedStatus) => set({ feedStatus }),
  setPanelLeftOpen: (panelLeftOpen) => set({ panelLeftOpen }),
  markFallbackActive: () => set({ fallbackActive: true }),
}));
